import Badge from "@adsk/alloy-react-badge";
import Theme from "@adsk/alloy-react-theme";
import React, { useMemo, useCallback, useRef } from "react";
import Tooltip from "@adsk/alloy-react-tooltip";
import { useTranslations } from "@adsk/acc-issues-translations";
import { Issue } from "@adsk/acc-issues-types";
import { useSheetAssociationForIssue } from "@adsk/acc-issues-common/hooks/data/useSheetAssociationForIssue";
import { FetchStatus } from "@adsk/acc-issues-core/state/redux/fetchHelpers";
import { usePlacementCreatedAtRevision } from "../../utils/hooks/usePlacementCreatedAtRevision";
import styled from "styled-components";

const StyledBadge = styled(Badge)`
  border: 1px solid ${Theme.colors.charcoal200};
  font-weight: 200;
  line-height: 16px;
  text-wrap-mode: nowrap;
`;

interface RevisionBadgeViewProps {
  latestRevision?: number;
  createdOnRevision?: number;
  createdOnRevisionFetchStatus?: FetchStatus;
  onHover?: () => void;
  container?: React.MutableRefObject<null | HTMLElement> | HTMLElement | null;
}

const RevisionBadgeView = ({
  latestRevision,
  createdOnRevision,
  createdOnRevisionFetchStatus,
  onHover,
  container,
}: RevisionBadgeViewProps) => {
  const { T } = useTranslations();

  const tooltipContent = useMemo(() => {
    if (createdOnRevision) {
      return T.translate("versionPill.createdOnVersion", {
        version: createdOnRevision,
      }) as string;
    }
    if (
      createdOnRevisionFetchStatus === FetchStatus.Success ||
      createdOnRevisionFetchStatus === FetchStatus.Error
    ) {
      return T.translate("versionPill.createdOnUnknown") as string;
    }
    return T.translate("loading") as string;
  }, [T, createdOnRevision, createdOnRevisionFetchStatus]);

  const containerElement =
    container && "current" in container ? container.current : container;

  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;

  const handleMouseEnter = useCallback(() => {
    onHoverRef.current?.();
  }, []);

  if (!latestRevision) {
    return null;
  }

  return (
    <Tooltip
      placement={Tooltip.PLACEMENTS.TOP}
      variant={Tooltip.VARIANTS.DARK}
      content={tooltipContent}
      container={containerElement}
    >
      <StyledBadge
        color={Theme.colors.white}
        textColor={Theme.colors.black}
        data-testid="created-on-version-badge"
        onMouseEnter={handleMouseEnter}
      >
        {T.translate("versionPill.version", {
          version: latestRevision,
        })}
      </StyledBadge>
    </Tooltip>
  );
};

export const PlacementRevisionBadge = ({
  issue,
  container,
}: {
  issue?: Issue;
  container?: React.MutableRefObject<null | HTMLElement> | HTMLElement | null;
}) => {
  const { document } = useSheetAssociationForIssue({ issue });
  const latestRevision = document?.version?.revisionNumber;

  const { revision, fetchCreatedAtRevision, fetchStatus } =
    usePlacementCreatedAtRevision(issue);

  return (
    <RevisionBadgeView
      latestRevision={latestRevision}
      createdOnRevision={revision}
      createdOnRevisionFetchStatus={fetchStatus}
      onHover={fetchCreatedAtRevision}
      container={container}
    />
  );
};
